"use client";

import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";
import { activeTicket, additionalCashGbp, formatGbp, preSalePrice, prizeTitle } from "../../lifestyle-constants";
import AddToCartButton from "./AddToCartButton";

interface PrizeCardProps {
  prize: LifestylePrize;
  onAddToCart: (prize: LifestylePrize) => void;
  /** Eager-load the first row so the grid does not pop in. */
  isPriority?: boolean;
}

export default function PrizeCard({ prize, onAddToCart, isPriority = false }: PrizeCardProps) {
  const { primary, secondary } = prizeTitle(prize);
  const ticket = activeTicket(prize);
  const wasPrice = preSalePrice(prize);
  const bonusCash = additionalCashGbp(prize);
  const name = [primary, secondary].filter(Boolean).join(" ");
  const showCashAlternative = prize.HasCashAlternative && !prize.HideCashAlternative && Boolean(prize.CashAlternative);

  return (
    // Square and unoutlined, as drawn: the card reads as its own block because
    // the page behind it is grey, not because it carries a border.
    <div className="flex flex-col w-full h-full min-h-[263px] lg:min-h-[289px] p-3 bg-white transition-shadow hover:shadow-md">
      {/* One link covers the informational block so the cart button stays a
          separate, independently focusable control. */}
      {/* Spacing is per-element rather than a uniform gap, which is what makes
          the card land on its drawn height at both breakpoints: the rows are
          19/21/13/16px tall above an image well that the image itself sizes. */}
      <a href={prize.Url} className="flex flex-col flex-1 min-w-0">
        <div className="text-base font-bold leading-[19px] lg:leading-[25px] text-gray-1 truncate">{primary}</div>

        <div className="pt-[5px] text-xs lg:text-[13.5px] leading-4 text-gray-5 truncate">
          {secondary}
          {bonusCash > 0 && <> + {formatGbp(bonusCash)}</>}
        </div>

        <hr className="!mx-0 !my-1.5 border-gray-8" />

        <p className={`text-xs tracking-[0] text-gray-1 ${showCashAlternative ? "visible" : "invisible"}`}>
          Or take <span className="font-semibold">{prize.CashAlternative}</span> cash
        </p>

        {/* The "POPULAR" and "PLUS £x,000" flashes are composited into the
            artwork by the CMS, so the card draws no overlays of its own. */}
        <div className="flex items-center justify-center flex-1 min-h-[130px] py-[15px]">
          {/* The well is aspect-locked rather than the image, so the card keeps
              its drawn height whatever shape the artwork is: a tall photo would
              otherwise stretch the well to its 120px cap and, on mobile, push
              the card 20px past the design. `object-contain` keeps each image's
              own proportions inside it. */}
          <div className="relative aspect-[394/240] w-full max-w-[197px] max-h-[120px]">
            <img
              src={prize.ThumbnailImage}
              alt={name}
              className="absolute inset-0 w-full h-full object-contain"
              loading={isPriority ? "eager" : "lazy"}
            />
          </div>
        </div>
      </a>

      <div className="flex items-center justify-between w-full gap-2">
        <div className="flex items-baseline min-w-0 gap-[7px]">
          <span className="text-sm font-medium text-secondary">{ticket?.FormattedPrice ?? ""}</span>
          {wasPrice && <span className="text-sm text-gray-5 line-through">{wasPrice}</span>}
        </div>
        <AddToCartButton prizeName={name} isDisabled={!prize.UserCanPurchase} onClick={() => onAddToCart(prize)} />
      </div>
    </div>
  );
}
